/**
 * **A movement's trend in one line** — what a small chart beside a clip or a strength goal draws:
 * the movement's primary line (the first its fields measure, e1RM for a lift with reps), session by
 * session, and the latest figure on it. The Forma tab reads the same sessions on the same line
 * catalogue; this is the smallest reading of them, without the two dates.
 *
 * Pure: no React, no query hook. `t` and the locale arrive through `LineWordsDeps`.
 */

import {
  lineReadings,
  movementLines,
  sizedLineWords,
  type LiftFields,
  type LiftLine,
  type LineWordsDeps,
} from './liftLines';
import { sessionFigure, type LiftSession } from './liftSessions';

export interface LiftTrend {
  readonly line: LiftLine;
  /** „e1RM", „Dystans" — the line's own name. */
  readonly name: string;
  /** The unit word the chart prints beside a figure; empty for a time. */
  readonly unit: string;
  readonly better: 'higher' | 'lower';
  /** Present when the chart must print its figures as a clock rather than as numbers. */
  readonly figure: ((value: number) => string) | null;
  readonly points: readonly { readonly x: Date; readonly y: number }[];
  /** The newest session's figure with its unit, „92,5 kg". */
  readonly latest: string;
}

/**
 * `null` where there is nothing to draw: no session carries a figure on the movement's line. One
 * session draws one point — the reading is still the client's.
 */
export function liftTrend(
  sessions: readonly LiftSession[],
  fields: LiftFields,
  deps: LineWordsDeps,
): LiftTrend | null {
  const { line } = movementLines(fields, sessions, 'e1rm');
  if (line === null) return null;
  const words = sizedLineWords(sessions, line, deps);
  const readings = lineReadings(sessions, line, words);
  const newest = [...sessions].reverse().find((session) => sessionFigure(session, line) !== null);
  const raw = newest === undefined ? null : sessionFigure(newest, line);
  if (readings.length === 0 || raw === null) return null;
  return {
    line,
    name: deps.t(`web:forma.line.${line}`),
    unit: words.unit,
    better: words.chartBetter,
    figure: words.clock ? words.chartFigure : null,
    points: readings.map((reading) => ({ x: new Date(reading.at), y: reading.value })),
    latest: words.withUnit(raw),
  };
}
